import { useState } from 'react';
import { formatCurrency } from '../../lib/format';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';
import { useExternalValueSync } from '../../hooks/useExternalValueSync';

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  productId?: string;
}

interface DraftInvoice {
  _id: string;
  invoiceNumber: string;
  items: InvoiceItem[];
  shippingAmount: number;
  subtotal: number;
  totalAmount: number;
  // Charges this modal cannot edit but which are part of the total. They must
  // still be in the preview, or the Total shown here disagrees with the total
  // the server recomputes on save (Invoice pre-save hook).
  telemedNetworkFee?: number;
  taxAmount?: number;
  volumeReward?: {
    tierLabel?: string;
    discountPercent?: number;
    discountAmount?: number;
    freeShippingApplied?: boolean;
    appliedAs?: 'discount' | 'physician_markup';
  };
}

interface Props {
  invoice: DraftInvoice;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditDraftModal({ invoice, onClose, onSuccess }: Props) {
  const [items, setItems] = useState<InvoiceItem[]>(
    invoice.items.map(i => ({ ...i }))
  );
  const [shippingAmount, setShippingAmount] = useState(invoice.shippingAmount);
  // Uncontrolled, but still following an externally changed value. The previous
  // key={`shipping-${shippingAmount}`} form remounted the field on every
  // keystroke — this input's own onChange sets shippingAmount — so the cursor
  // left after a single character.
  const shippingRef = useExternalValueSync<HTMLInputElement>(shippingAmount);
  const [submitting, setSubmitting] = useState(false);

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const next = [...items];
    const item = { ...next[index], [field]: value };
    if (field === 'quantity' || field === 'unitPrice') {
      item.totalPrice = (item.quantity || 0) * (item.unitPrice || 0);
    }
    next[index] = item;
    setItems(next);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) {
      toast.error('Invoice must have at least one item');
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unitPrice: 0, totalPrice: 0 }]);
  };

  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  // Mirrors the Invoice pre-save hook: subtotal + shipping + telemed network fee
  // + tax − reward discount. A 'physician_markup' reward is physician earnings,
  // not a discount, so it never comes off the total; free shipping zeroes the
  // shipping line in the total only.
  const telemedNetworkFee = invoice.telemedNetworkFee || 0;
  const taxAmount = invoice.taxAmount || 0;
  const rewardDiscount = invoice.volumeReward?.appliedAs === 'physician_markup'
    ? 0
    : (invoice.volumeReward?.discountAmount || 0);
  const shippingApplied = invoice.volumeReward?.freeShippingApplied ? 0 : (shippingAmount || 0);
  const totalAmount = subtotal + shippingApplied + telemedNetworkFee + taxAmount - rewardDiscount;

  const handleSubmit = async () => {
    if (items.some(i => !i.description.trim())) {
      toast.error('All items must have a description');
      return;
    }
    if (items.some(i => i.quantity <= 0 || i.unitPrice < 0)) {
      toast.error('All items must have valid quantity and price');
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiClient.put(`/credit-memos/invoices/${invoice._id}/edit-draft`, {
        items: items.map(i => ({
          description: i.description,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          totalPrice: i.quantity * i.unitPrice,
          productId: i.productId
        })),
        shippingAmount
      });

      if (response.success) {
        toast.success('Draft invoice updated');
        onSuccess();
        onClose();
      } else {
        toast.error(response.error || 'Failed to update draft');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update draft');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]" onClick={onClose}>
      <div className="relative top-10 mx-auto p-0 w-full max-w-lg shadow-lg rounded-lg bg-white" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Edit Draft Invoice</h3>
            <p className="text-sm text-gray-500">{invoice.invoiceNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* Line items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Line Items</p>
              <button
                onClick={addItem}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> Add Item
              </button>
            </div>

            {items.map((item, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <input
                    type="text"
                    value={item.description}
                    onChange={e => updateItem(idx, 'description', e.target.value)}
                    placeholder="Description"
                    className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                  {items.length > 1 && (
                    <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 p-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <div className="w-20">
                    <label className="text-[10px] text-gray-500 uppercase">Qty</label>
                    <input
                      type="number"
                      defaultValue={item.quantity}
                      key={`qty-${idx}`}
                      min={1}
                      onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) updateItem(idx, 'quantity', v); }}
                      onBlur={e => { const v = parseInt(e.target.value); const c = isNaN(v) || v < 1 ? 1 : v; updateItem(idx, 'quantity', c); e.target.value = String(c); }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div className="w-28">
                    <label className="text-[10px] text-gray-500 uppercase">Unit Price</label>
                    <input
                      type="number"
                      defaultValue={item.unitPrice}
                      key={`price-${idx}`}
                      step="0.01"
                      min={0}
                      onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) updateItem(idx, 'unitPrice', v); }}
                      onBlur={e => { const v = parseFloat(e.target.value); const c = isNaN(v) || v < 0 ? 0 : v; updateItem(idx, 'unitPrice', c); e.target.value = c.toFixed(2); }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <span className="text-sm font-mono text-gray-600">= {formatCurrency(item.quantity * item.unitPrice)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Shipping */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Shipping</label>
            <input
              type="number"
              ref={shippingRef}
              defaultValue={shippingAmount}
              step="0.01"
              min={0}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) setShippingAmount(v); }}
              onBlur={e => { const v = parseFloat(e.target.value); const c = isNaN(v) || v < 0 ? 0 : v; setShippingAmount(c); e.target.value = c.toFixed(2); }}
              className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-mono">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Shipping:</span>
              <span className="font-mono">
                {invoice.volumeReward?.freeShippingApplied
                  ? <span className="text-indigo-600">waived</span>
                  : formatCurrency(shippingAmount)}
              </span>
            </div>
            {telemedNetworkFee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Telemed network fee:</span>
                <span className="font-mono">{formatCurrency(telemedNetworkFee)}</span>
              </div>
            )}
            {taxAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax:</span>
                <span className="font-mono">{formatCurrency(taxAmount)}</span>
              </div>
            )}
            {rewardDiscount > 0 && (
              <div className="flex justify-between text-sm text-indigo-600">
                <span>Volume Reward{invoice.volumeReward?.tierLabel ? ` (${invoice.volumeReward.tierLabel})` : ''}:</span>
                <span className="font-mono">−{formatCurrency(rewardDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-bold border-t pt-1">
              <span>Total:</span>
              <span className="font-mono">{formatCurrency(totalAmount)}</span>
            </div>
            {totalAmount !== invoice.totalAmount && (
              <p className="text-xs text-amber-600 mt-1">
                Changed from {formatCurrency(invoice.totalAmount)} to {formatCurrency(totalAmount)}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || totalAmount <= 0}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Save className="h-3 w-3" />
            {submitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
